
import { supabase } from "@/integrations/supabase/client";

export const createBucketIfNotExists = async () => {
  try {
    console.log("Checking if business-media bucket exists...");
    
    // Check if the bucket exists
    const { data: buckets, error: listError } = await supabase.storage.listBuckets();
    
    if (listError) {
      console.error("Error listing buckets:", listError);
      console.error("Error details:", JSON.stringify(listError));
      return { success: false, error: listError };
    }
    
    const bucketExists = buckets?.some(bucket => bucket.name === 'business-media');
    
    if (!bucketExists) {
      console.log("Business-media bucket doesn't exist, attempting to create it...");
      
      // Create with public access - CRITICAL for RLS
      const { error } = await supabase.storage.createBucket('business-media', {
        public: true, // MUST be public to avoid RLS issues
        fileSizeLimit: 10 * 1024 * 1024 // 10MB limit
      });
      
      if (error) {
        if (error.message.includes('already exists')) {
          // Bucket was created by another request, this is fine
          console.log("Bucket already exists (concurrent creation)");
          return { success: true, exists: true };
        }
        
        console.error("Error creating bucket:", error);
        console.error("Error details:", JSON.stringify(error));
        return { success: false, error };
      }
      
      console.log("Successfully created business-media bucket");
      
      // Additional delay for bucket policies to apply
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // Explicitly verify and update bucket public status
      try {
        const { error: updateError } = await supabase.storage.updateBucket('business-media', {
          public: true, // Force update to public again
          fileSizeLimit: 10 * 1024 * 1024
        });
        
        if (updateError) {
          console.warn("Could not update bucket settings after creation:", updateError);
          console.warn("Error details:", JSON.stringify(updateError));
        } else {
          console.log("Successfully updated bucket to be public");
        }
      } catch (updateErr) {
        console.warn("Error during bucket update verification:", updateErr);
        console.warn("Error details:", JSON.stringify(updateErr));
      }
      
      return { success: true, created: true };
    }
    
    // Bucket exists, ensure it's public
    console.log("Bucket exists, ensuring it's public...");
    
    const { error: updateError } = await supabase.storage.updateBucket('business-media', {
      public: true, // CRITICAL for RLS
      fileSizeLimit: 10 * 1024 * 1024
    });
    
    if (updateError) {
      console.warn("Could not update bucket settings:", updateError);
      console.warn("Error details:", JSON.stringify(updateError));
      // Continue anyway, as the bucket exists
    } else {
      console.log("Successfully updated bucket to be public");
    }
    
    // Verify bucket accessibility by attempting to list its contents
    const { data: listData, error: accessError } = await supabase.storage
      .from('business-media')
      .list('', { limit: 1 });
      
    if (accessError) {
      console.error("Cannot access business-media bucket:", accessError);
      console.error("Error details:", JSON.stringify(accessError));
      return { 
        success: false, 
        error: new Error("Cannot access storage bucket. Storage may not be properly configured.") 
      };
    }
    
    console.log("Business-media bucket exists, is public, and is accessible");
    console.log("Bucket contents:", listData);
    return { success: true, exists: true };
  } catch (error) {
    console.error("Unexpected error checking/creating bucket:", error);
    console.error("Error details:", JSON.stringify(error));
    return { 
      success: false, 
      error: new Error("System error when preparing storage. Please try again later.")
    };
  }
};

export const uploadMedia = async (files: File[], businessId: string) => {
  const mediaUrls: string[] = [];
  const uploadErrors: string[] = [];
  
  console.log(`Starting upload of ${files.length} files for business ID ${businessId}`);
  
  try {
    // Check if the bucket exists and is accessible
    const bucketCheck = await createBucketIfNotExists();
    console.log("Bucket check result:", bucketCheck);
    
    if (!bucketCheck.success) {
      console.error("Failed to ensure bucket exists:", bucketCheck.error);
      const errorMessage = bucketCheck.error instanceof Error 
        ? bucketCheck.error.message 
        : "Failed to prepare storage for uploads. Please try again or contact support.";
      
      return { 
        mediaUrls: [], 
        uploadErrors: [errorMessage] 
      };
    }
    
    // Log the path we're going to use
    console.log(`Will upload files to path: ${businessId}/`);
    
    for (const file of files) {
      try {
        const fileExt = file.name.split('.').pop();
        // Use a more predictable filename pattern with just timestamp
        const fileName = `${businessId}/${Date.now()}.${fileExt}`;
        
        console.log(`Uploading file: ${file.name} (${(file.size / (1024 * 1024)).toFixed(2)} MB) of type ${file.type} to path: ${fileName}`);
        
        // Upload with explicit content type and public: true option
        const { error: uploadError, data } = await supabase.storage
          .from('business-media')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: true,
            contentType: file.type, // Explicitly set content type
          });
          
        if (uploadError) {
          console.error("Error uploading file:", file.name, uploadError);
          console.error("Error details:", JSON.stringify(uploadError));
          uploadErrors.push(`Failed to upload ${file.name}: ${uploadError.message}`);
          continue;
        }
        
        console.log(`File uploaded successfully: ${fileName}`);
        
        // Get the public URL
        const publicUrlData = supabase.storage
          .from('business-media')
          .getPublicUrl(fileName);
          
        console.log("Public URL data:", publicUrlData);
        
        if (publicUrlData?.data?.publicUrl) {
          mediaUrls.push(publicUrlData.data.publicUrl);
          console.log(`Public URL generated: ${publicUrlData.data.publicUrl}`);
        } else {
          console.warn(`Could not generate public URL for ${fileName}`);
          uploadErrors.push(`Generated URL was empty for ${file.name}`);
        }
      } catch (error: any) {
        console.error(`Unexpected error uploading ${file.name}:`, error);
        console.error("Error details:", JSON.stringify(error));
        uploadErrors.push(`Failed to upload ${file.name}: ${error?.message || 'Unexpected error'}`);
      }
    }
    
    console.log(`Upload process completed with ${mediaUrls.length} successful uploads and ${uploadErrors.length} failures`);
    console.log("Media URLs:", mediaUrls);
    console.log("Upload errors:", uploadErrors);
    
    return { mediaUrls, uploadErrors };
  } catch (error: any) {
    console.error("Fatal error during upload process:", error);
    console.error("Error details:", JSON.stringify(error));
    return { 
      mediaUrls: [],
      uploadErrors: [`Upload process failed: ${error?.message || 'Unknown error'}`]
    };
  }
};
